import { useCallback, useEffect, useRef, useState } from "react";

const VERSION_MANIFEST_FALLBACK = "/version.json";
const POLL_INTERVAL_MS = 5 * 60 * 1000;
const GRACE_PERIOD_MS = 60 * 60 * 1000;
const DISMISS_COOLDOWN_MS = 2 * 60 * 60 * 1000;
const DISMISS_STORAGE_KEY = "carrie:version-dismissed-until";

type VersionManifest = { version: string };

function resolveManifestUrl(): string {
  const link = document.getElementById("carrie-version-manifest");
  if (link instanceof HTMLLinkElement && link.href) return link.href;
  return VERSION_MANIFEST_FALLBACK;
}

async function fetchRemoteVersion(): Promise<string | null> {
  try {
    const url = new URL(resolveManifestUrl(), window.location.href);
    url.searchParams.set("_", String(Date.now()));
    const res = await fetch(url.toString(), {
      cache: "no-store",
      credentials: "omit",
    });
    if (!res.ok) return null;
    const data = (await res.json()) as Partial<VersionManifest>;
    return typeof data.version === "string" ? data.version : null;
  } catch {
    return null;
  }
}

function readDismissedUntil(): number {
  try {
    const raw = window.localStorage.getItem(DISMISS_STORAGE_KEY);
    return raw ? Number.parseInt(raw, 10) || 0 : 0;
  } catch {
    return 0;
  }
}

function writeDismissedUntil(value: number) {
  try {
    window.localStorage.setItem(DISMISS_STORAGE_KEY, String(value));
  } catch {
    // ignore
  }
}

export type VersionCheckState = {
  /** A new version was detected but we're within the grace period — refresh silently on next route change. */
  pendingRefresh: boolean;
  /** Grace period expired with no route change — show the banner. */
  isVisible: boolean;
  dismiss: () => void;
  refresh: () => void;
};

export function useVersionCheck(): VersionCheckState {
  const initialVersionRef = useRef<string>(__APP_VERSION__);
  const [newVersion, setNewVersion] = useState<string | null>(null);
  const [detectedAt, setDetectedAt] = useState<number | null>(null);
  const [graceExpired, setGraceExpired] = useState(false);
  const [dismissedUntil, setDismissedUntil] = useState<number>(() =>
    readDismissedUntil(),
  );
  const hasNewVersion = newVersion != null;

  const check = useCallback(async () => {
    const remote = await fetchRemoteVersion();
    if (!remote) return;
    if (remote !== initialVersionRef.current) {
      setNewVersion((prev) => {
        if (prev === remote) return prev;
        setDetectedAt(Date.now());
        setGraceExpired(false);
        return remote;
      });
    }
  }, []);

  // Poll + visibility listener. Once a newer build is known the outcome is
  // settled — the grace timer below owns the rest, and re-reading the manifest
  // can only restart that timer when another deploy lands, pushing the banner
  // further out. So stop fetching and drop the listener after the first hit.
  useEffect(() => {
    if (hasNewVersion) return;

    void check();
    const intervalId = window.setInterval(() => {
      void check();
    }, POLL_INTERVAL_MS);

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") void check();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [check, hasNewVersion]);

  // Grace period timer — after GRACE_PERIOD_MS, transition to showing the banner
  useEffect(() => {
    if (detectedAt == null) return;

    const elapsed = Date.now() - detectedAt;
    const remaining = GRACE_PERIOD_MS - elapsed;

    if (remaining <= 0) {
      setGraceExpired(true);
      return;
    }

    const timerId = window.setTimeout(() => {
      setGraceExpired(true);
    }, remaining);

    return () => window.clearTimeout(timerId);
  }, [detectedAt]);

  const dismiss = useCallback(() => {
    const until = Date.now() + DISMISS_COOLDOWN_MS;
    writeDismissedUntil(until);
    setDismissedUntil(until);
  }, []);

  const refresh = useCallback(() => {
    writeDismissedUntil(0);
    window.location.reload();
  }, []);

  const pendingRefresh = hasNewVersion && !graceExpired;
  const isVisible =
    hasNewVersion && graceExpired && Date.now() >= dismissedUntil;

  return { pendingRefresh, isVisible, dismiss, refresh };
}
